/**
 * ImpersonationPicker — nav-bar dropdown that lets the DM "view as" a player.
 *
 * Loads the Cognito user list on first open (lazy, because the admin
 * endpoint is admin-group gated and we only mount this for admins).
 * Selecting a player activates impersonation; clicking "Return to DM"
 * stops it. The real admin identity is never hidden — the banner and
 * this control always remind the DM they're impersonating.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { listUsers, type AdminUser } from './adminApi';
import { isAdmin } from './auth';
import { useAuth } from './AuthContext';
import {
  useIdentity,
  adminUserToTarget,
} from './ImpersonationContext';

export default function ImpersonationPicker() {
  const { user } = useAuth();
  const { target, impersonating, startImpersonating, stopImpersonating } =
    useIdentity();

  const [open, setOpen] = useState(false);
  const [players, setPlayers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const admin = isAdmin(user);
  if (!admin) return null;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const handleOpen = useCallback(async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (players.length > 0) return; // already loaded
    setLoading(true);
    setError(null);
    try {
      const resp = await listUsers();
      // Filter out the current DM from the player list
      const others = resp.users.filter(
        (u) => u.sub !== user?.sub && u.enabled,
      );
      setPlayers(others);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load players');
    } finally {
      setLoading(false);
    }
  }, [open, players.length, user?.sub]);

  const handleSelect = useCallback(
    (p: AdminUser) => {
      startImpersonating(adminUserToTarget(p));
      setOpen(false);
    },
    [startImpersonating],
  );

  const handleReturn = useCallback(() => {
    stopImpersonating();
    setOpen(false);
  }, [stopImpersonating]);

  return (
    <div className="impersonation-picker" ref={dropdownRef}>
      <button
        className={`impersonation-picker__trigger${impersonating ? ' impersonation-picker__trigger--active' : ''}`}
        onClick={handleOpen}
        title={impersonating ? `Viewing as ${target?.username}` : 'View as player'}
      >
        {impersonating ? `👁 ${target?.username}` : '👁 View as…'}
      </button>

      {open && (
        <div className="impersonation-picker__dropdown">
          {impersonating && (
            <button
              className="impersonation-picker__item impersonation-picker__item--return"
              onClick={handleReturn}
            >
              Return to DM view
            </button>
          )}

          {loading && (
            <div className="impersonation-picker__item impersonation-picker__item--info">
              Loading players…
            </div>
          )}

          {error && (
            <div className="impersonation-picker__item impersonation-picker__item--error">
              {error}
            </div>
          )}

          {!loading && !error && players.length === 0 && (
            <div className="impersonation-picker__item impersonation-picker__item--info">
              No other players found
            </div>
          )}

          {players.map((p) => (
            <button
              key={p.sub}
              className={`impersonation-picker__item${target?.sub === p.sub ? ' impersonation-picker__item--selected' : ''}`}
              onClick={() => handleSelect(p)}
            >
              {p.email ?? p.sub.slice(0, 8)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
